import { applyPrintTokens, todayYmd, PRINT_ROWNO_FIELD } from './printLayout';

function cssEscape(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pageSizeCss(paper, orientation) {
  const p = paper === 'A3' ? 'A3' : 'A4';
  return orientation === 'portrait' ? p : `${p} landscape`;
}

function bandCellHtml(cell, ctx, extraClass = '') {
  if (!cell) return '<div class="zone"></div>';
  const text = applyPrintTokens(cell.text, ctx);
  const align = cell.align || 'left';
  const size = Number(cell.fontSizePt) || 9;
  return `<div class="zone ${extraClass}" style="text-align:${align};font-size:${size}pt">${cssEscape(text)}</div>`;
}

function stampTableHtml(stamp) {
  if (!stamp?.enabled) return '';
  const titles = stamp.titles || [];
  const heads = titles.map((t) => `<th>${cssEscape(t || '')}</th>`).join('');
  const marks = titles.map(() => '<td class="mark">&nbsp;</td>').join('');
  return `<table class="stamp"><thead><tr>${heads}</tr></thead><tbody><tr>${marks}</tr></tbody></table>`;
}

/**
 * 문서 설정 → 인쇄/PDF 미리보기 HTML (1차)
 */
export function buildPrintHtml(model, layout, ctx = {}) {
  const title = layout?.title || model?.gridLabel || '목록';
  const font = layout?.fontFamily || 'Pretendard';
  const fontSize = Number(layout?.fontSizePt) || 9;
  const paper = layout?.paper || 'A4';
  const orientation = layout?.orientation || 'landscape';
  const columns = model?.columns || [];
  const rows = model?.rows || [];
  const mergeMap = model?.mergeMap || null;
  const showNo = layout?.showRowNumber !== false;
  const margin = layout?.marginMm || { top: 15, right: 15, bottom: 15, left: 15 };
  const stamp = layout?.stamp || { enabled: false };
  const printCtx = {
    today: ctx.today || todayYmd(),
    screenName: ctx.screenName || model?.gridLabel || title,
    tenantName: ctx.tenantName || '',
    tenantCode: ctx.tenantCode || '',
    userName: ctx.userName || '',
    page: ctx.page ?? 1,
    pageCount: ctx.pageCount ?? 1,
  };

  const colCount = columns.length + (showNo ? 1 : 0);
  const widths = layout?.columnWidthsMm || {};
  const rowNoMm = Number(layout?.rowNumberWidthMm) || 12;
  const colGroup = `<colgroup>${
    showNo ? `<col data-field="${PRINT_ROWNO_FIELD}" style="width:${rowNoMm}mm" />` : ''
  }${columns
    .map((c) => {
      const w = Number(widths[c.field]);
      const style = Number.isFinite(w) && w > 0 ? ` style="width:${w}mm"` : '';
      return `<col data-field="${cssEscape(c.field)}"${style} />`;
    })
    .join('')}
  </colgroup>`;
  const headerCells = [
    showNo
      ? `<th class="no resize-col" data-field="${PRINT_ROWNO_FIELD}" data-width-mm="${rowNoMm}">No.</th>`
      : '',
    ...columns.map((c) => {
      const w = Number(widths[c.field]);
      const mmAttr = Number.isFinite(w) && w > 0 ? ` data-width-mm="${w}"` : '';
      return `<th class="resize-col" data-field="${cssEscape(c.field)}"${mmAttr}>${cssEscape(c.header || c.field)}</th>`;
    }),
  ].join('');

  const bodyRows = rows
    .map((row) => {
      const tds = [];
      if (showNo) tds.push(`<td class="no">${row.index + 1}</td>`);
      columns.forEach((c) => {
        const info = mergeMap?.[c.field]?.[row.index];
        if (info?.skip) return;
        const span = info?.rowSpan > 1 ? ` rowspan="${info.rowSpan}"` : '';
        const align = c.align === 'right' || c.type === 'number' ? 'right' : c.align || 'left';
        tds.push(
          `<td class="c-${align}"${span}>${cssEscape(row.cells[c.field] ?? '')}</td>`
        );
      });
      return `<tr>${tds.join('')}</tr>`;
    })
    .join('');

  const stampHtml = stampTableHtml(stamp);
  const stampInHeader = stamp.enabled && stamp.position === 'headerRight';
  const stampInTitle = stamp.enabled && stamp.position !== 'headerRight';

  const headerBand = `
    <div class="band">
      ${bandCellHtml(layout?.header?.left, printCtx)}
      ${bandCellHtml(layout?.header?.center, printCtx)}
      ${stampInHeader ? `<div class="zone right">${stampHtml}</div>` : bandCellHtml(layout?.header?.right, printCtx)}
    </div>`;

  const footerBand = `
    <div class="band footer-band">
      ${bandCellHtml(layout?.footer?.left, printCtx)}
      ${bandCellHtml(layout?.footer?.center, printCtx)}
      ${bandCellHtml(layout?.footer?.right, printCtx)}
    </div>`;

  const titleRow = `
    <div class="title-row">
      <h1 class="title">${cssEscape(title)}</h1>
      ${stampInTitle ? `<div class="stamp-wrap">${stampHtml}</div>` : ''}
    </div>`;

  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>${cssEscape(title)}</title>
  <style>
    @page {
      size: ${pageSizeCss(paper, orientation)};
      margin: ${margin.top}mm ${margin.right}mm ${margin.bottom}mm ${margin.left}mm;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: '${cssEscape(font)}', 'Malgun Gothic', sans-serif;
      font-size: ${fontSize}pt;
      color: #111;
    }
    .sheet { box-sizing: border-box; }
    .band {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px;
      margin-bottom: 6px;
      align-items: start;
    }
    .zone { min-height: 1.2em; word-break: break-word; }
    .title-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin: 4px 0 10px;
    }
    .title {
      flex: 1;
      text-align: center;
      font-size: ${fontSize + 5}pt;
      font-weight: 700;
      margin: 0;
    }
    .stamp-wrap { flex: 0 0 auto; }
    table.stamp {
      border-collapse: collapse;
      font-size: ${Math.max(8, fontSize - 1)}pt;
    }
    table.stamp th, table.stamp td {
      border: 1px solid #222;
      padding: 2px 8px;
      text-align: center;
    }
    table.stamp th { font-weight: 600; background: #f3f3f3; min-width: 52px; }
    table.stamp td.mark { height: 36px; }
    table.list {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    table.list th, table.list td {
      border: 1px solid #222;
      padding: 3px 6px;
      vertical-align: middle;
      word-break: break-word;
    }
    table.list th {
      background: #eee;
      font-weight: 600;
      text-align: center;
      position: relative;
    }
    table.list th.resize-col::after {
      content: '';
      position: absolute;
      top: 0;
      right: -3px;
      width: 6px;
      height: 100%;
      cursor: col-resize;
    }
    td.no, th.no { text-align: center; white-space: nowrap; }
    td.c-right { text-align: right; }
    td.c-center { text-align: center; }
    td.c-left { text-align: left; }
    .footer-band { margin-top: 10px; }
    @media print {
      .sheet { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    ${headerBand}
    ${titleRow}
    <table class="list">
      ${colGroup}
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows || `<tr><td colspan="${Math.max(1, colCount)}">조회된 자료가 없습니다</td></tr>`}</tbody>
    </table>
    ${footerBand}
  </div>
</body>
</html>`;
}

export function openPrintWindow(html) {
  const w = window.open('', '_blank', 'noopener,noreferrer');
  if (!w) return { ok: false, reason: '팝업이 차단되었습니다. 브라우저에서 팝업을 허용하세요.' };
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  const run = () => {
    try {
      w.print();
    } catch {
      /* ignore */
    }
  };
  if (w.document.readyState === 'complete') {
    setTimeout(run, 50);
  } else {
    w.onload = () => setTimeout(run, 50);
  }
  return { ok: true };
}
